'use client'
import FloatingParticlesBackground from '@/app/[locale]/(create)/partner/components/FloatingParticlesBackground'
import IconViverseLogo from '@/components/assets/icon_viverse_logo.svg'
import ViverseButton from '@/components/ViverseButton'
import { usePartnerApply } from '@/hooks/usePartnerApply'
import ImageViverseLogo from '@/public/images/viverse-logo.png'
import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

export default function ComparisonSection() {
  const t = useTranslations()
  const { handleClickApplyButton } = usePartnerApply()

  const tableData = [
    {
      key: 'webGaming',
      platform: t('partner.comparison.platforms.web-gaming.name'),
      rate: t('partner.comparison.platforms.web-gaming.rate'),
      rateDetailed: t('partner.comparison.platforms.web-gaming.rate'),
      viewMetric: t('partner.comparison.platforms.web-gaming.view-metric'),
      playPercentage: '30%',
      earnings: t('partner.comparison.platforms.web-gaming.earnings'),
    },
    {
      key: 'videoSharing',
      platform: t('partner.comparison.platforms.video-sharing.name'),
      rate: t('partner.comparison.platforms.video-sharing.rate'),
      rateDetailed: t('partner.comparison.platforms.video-sharing.rate'),
      viewMetric: t('partner.comparison.platforms.video-sharing.view-metric'),
      playPercentage: '45%',
      earnings: t('partner.comparison.platforms.video-sharing.earnings'),
    },
  ]

  const mobileTableData = [
    {
      key: 'webGaming',
      platform: t('partner.comparison.platforms.web-gaming.name'),
      rate: t('partner.comparison.platforms.web-gaming.mobile.rate'),
      viewMetric: t('partner.comparison.platforms.web-gaming.view-metric'),
      playPercentage: '30%',
      earnings: t('partner.comparison.platforms.web-gaming.earnings'),
    },
    {
      key: 'videoSharing',
      platform: t('partner.comparison.platforms.video-sharing.name'),
      rate: t('partner.comparison.platforms.video-sharing.mobile.rate'),
      viewMetric: t('partner.comparison.platforms.video-sharing.view-metric'),
      playPercentage: '45%',
      earnings: t('partner.comparison.platforms.video-sharing.earnings'),
    },
  ]

  return (
    <section className="relative overflow-hidden bg-linear-to-b from-[#121216] to-[#131b26] py-12 lg:py-20">
      <div className="relative mx-auto flex max-w-full flex-col items-center gap-6 px-6 md:max-w-[750px] md:gap-10 lg:max-w-[856px]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="flex flex-col items-center gap-3 md:gap-4"
        >
          <h2 className="text-center text-[32px] leading-9 font-extrabold tracking-[0.15px] text-white md:text-[38px] md:leading-10">
            {t('partner.comparison.title')}
          </h2>
          <p className="text-vive-primary-20 text-center text-[16px] leading-6 tracking-[0.25px] md:text-[18px] md:leading-8">
            {t('partner.comparison.subtitle')}
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <ViverseButton
            size="medium"
            variant="colorful"
            className="min-h-12 lg:hidden"
            onClick={handleClickApplyButton}
          >
            {t('partner.comparison.apply-button')}
          </ViverseButton>
        </motion.div>

        <div
          className="w-full overflow-hidden rounded-2xl border border-[rgba(255,255,255,0.06)] bg-[rgba(26,32,44,0.4)] md:rounded-[20px] lg:min-w-[853px]"
          role="table"
        >
          {/* Mobile Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="border-b border-[rgba(255,255,255,0.06)] px-4 py-3 md:hidden"
            role="rowheader"
          >
            <div className="flex items-center justify-between gap-3 text-[11px]">
              <div className="w-[75px] min-w-12 font-semibold tracking-[0.6px] text-[#8b95a8] uppercase">
                {t('partner.comparison.table.platform-header')}
              </div>
              <div className="w-8 min-w-8 text-center font-semibold tracking-[0.6px] text-[#8b95a8] uppercase">
                {t('partner.comparison.table.mobile.rate-header')}
              </div>
              <div className="w-12 min-w-12 text-center font-semibold tracking-[0.6px] text-[#8b95a8] uppercase">
                {t('partner.comparison.table.mobile.view-metric-header')}
              </div>
              <div className="w-8 min-w-8 text-center font-semibold tracking-[0.6px] text-[#8b95a8] uppercase">
                %
              </div>
              <div className="w-16 min-w-16 text-right font-semibold tracking-[0.6px] text-[#8b95a8] uppercase">
                {t('partner.comparison.table.mobile.monthly-earnings-header')}
              </div>
            </div>
          </motion.div>

          {/* Mobile Table Body */}
          <div className="md:hidden">
            {mobileTableData.map((item, index) => (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                  duration: 0.75,
                  ease: [0.22, 1, 0.36, 1],
                  delay: index * 0.06,
                }}
                className="border-b border-[rgba(255,255,255,0.04)] px-4 py-4 transition-colors"
                role="row"
              >
                <div className="flex items-center justify-between gap-3 text-[13px]">
                  <div className="text-vive-primary-20 w-[75px] min-w-12 font-medium wrap-break-word">
                    {item.platform}
                  </div>
                  <div className="w-8 min-w-8 text-center font-normal text-[#94a3b8]">
                    {item.rate}
                  </div>
                  <div className="w-12 min-w-12 text-center font-normal text-[#94a3b8]">
                    {item.viewMetric}
                  </div>
                  <div className="w-8 min-w-8 text-center font-normal text-[#94a3b8]">
                    {item.playPercentage}
                  </div>
                  <div className="w-16 min-w-16 text-right font-bold text-[#e8eef5]">
                    {item.earnings}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile VIVERSE Highlight Row */}
          <div
            className="border-t border-[rgba(0,160,203,0.2)] bg-[linear-gradient(169.151deg,rgba(0,160,203,0.06)_0%,rgba(65,95,248,0.04)_50%,rgba(237,100,237,0.06)_100%)] px-4 py-4 md:hidden md:px-8 md:py-8"
            role="row"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.12 }}
              className="flex items-center justify-between gap-3"
            >
              <div className="relative h-7 w-[75px] min-w-12">
                <Image src={ImageViverseLogo} alt="VIVERSE logo" width={32} height={32} />
              </div>
              <div className="w-8 min-w-8 text-center text-[13px] font-semibold text-[#00d3f2]">
                {t('partner.comparison.viverse.mobile.rate')}
              </div>
              <div className="w-12 min-w-12 text-center text-[12px] font-bold text-[#00d3f2]">
                {t('partner.comparison.viverse.view-metric')}
              </div>
              <div className="w-8 min-w-8 text-[13px] font-bold text-[#00d3f2]">100%</div>
              <div className="gradient-title-2 w-16 max-w-16 text-right text-[20px] font-extrabold">
                {t('partner.comparison.viverse.earnings')}
              </div>
            </motion.div>
          </div>

          {/* Tablet & Desktop Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="hidden items-center justify-between border-b border-[rgba(255,255,255,0.04)] px-8 py-6 transition-colors hover:bg-black/10 md:flex"
          >
            <div className="w-[147px] text-[12px] font-semibold tracking-[0.8px] text-[#8b95a8] uppercase lg:w-[165px]">
              {t('partner.comparison.table.platform-header')}
            </div>
            <div className="w-[100px] text-center text-[12px] font-semibold tracking-[0.8px] text-[#8b95a8] uppercase lg:w-[120px]">
              {t('partner.comparison.table.rate-header')}
            </div>
            <div className="w-[102px] text-center text-[12px] font-semibold tracking-[0.8px] text-[#8b95a8] uppercase">
              {t('partner.comparison.table.view-metric-header')}
            </div>
            <div className="w-[114px] text-center text-[12px] font-semibold tracking-[0.8px] text-[#8b95a8] uppercase lg:w-[148px]">
              {t('partner.comparison.table.play-percentage-header')}
            </div>
            <div className="w-[129px] text-right text-[12px] font-semibold tracking-[0.8px] text-[#8b95a8] uppercase">
              {t('partner.comparison.table.monthly-earnings-header')}
            </div>
          </motion.div>

          {/* Tablet & Desktop Table Body */}
          <div className="hidden md:block">
            {tableData.map((item, index) => (
              <motion.div
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                  duration: 0.75,
                  ease: [0.22, 1, 0.36, 1],
                  delay: index * 0.06,
                }}
                key={item.key}
                className="flex items-center justify-between border-b border-[rgba(255,255,255,0.04)] px-8 py-6 transition-colors hover:bg-black/10"
                role="row"
              >
                <div className="w-[147px] text-[16px] leading-6 font-medium text-[#d5e2f2] lg:w-[165px]">
                  {item.platform}
                </div>
                <div className="w-[100px] text-center text-[14px] leading-[22px] font-normal text-[#94a3b8] lg:w-[120px]">
                  {item.rateDetailed}
                </div>
                <div className="w-[102px] text-center text-[14px] leading-[22px] font-normal text-[#94a3b8]">
                  {item.viewMetric}
                </div>
                <div className="w-[114px] text-center text-[14px] leading-[22px] font-normal text-[#94a3b8] lg:w-[148px]">
                  {item.playPercentage}
                </div>
                <div className="w-[132px] text-right text-[28px] leading-8 font-bold text-[#e8eef5]">
                  {item.earnings}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Tablet & Desktop VIVERSE Highlight Row */}
          <div
            className="hidden border-t border-[rgba(0,160,203,0.2)] bg-[linear-gradient(169.151deg,rgba(0,160,203,0.06)_0%,rgba(65,95,248,0.04)_50%,rgba(237,100,237,0.06)_100%)] px-4 py-4 md:block md:px-8 md:py-8"
            role="row"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.12 }}
              className="flex items-center justify-between"
              role="row"
            >
              <div className="flex h-7 w-[147px] items-center gap-2 lg:w-[165px]">
                <IconViverseLogo className="" />
              </div>
              <div className="w-[100px] text-center text-[16px] leading-6 font-semibold text-[#00d3f2] lg:w-[120px]">
                {t('partner.comparison.viverse.rate')}
              </div>
              <div className="w-[102px] text-center text-[16px] leading-6 font-semibold text-[#00d3f2]">
                {t('partner.comparison.viverse.view-metric')}
              </div>
              <div className="w-[114px] text-center text-[16px] leading-6 font-semibold text-[#00d3f2] lg:w-[148px]">
                100%
              </div>
              <div className="gradient-title-2 w-[132px] text-right text-[42px] leading-[42px] font-extrabold">
                {t('partner.comparison.viverse.earnings')}
              </div>
            </motion.div>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <ViverseButton
            size="medium"
            variant="colorful"
            className="hidden min-h-12 lg:block"
            onClick={handleClickApplyButton}
          >
            {t('partner.comparison.apply-button')}
          </ViverseButton>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <p className="text-vive-primary-30 text-center text-[14px] leading-[22px] tracking-[0.2px]">
            {t('partner.comparison.footer-text')}
          </p>
        </motion.div>
      </div>
      <FloatingParticlesBackground />
    </section>
  )
}
